import React from 'react';
import { FieldType } from 'types/FieldTypes';
import FormModal from 'components/modals/FormModal';

// Rename the name of the function:
const LocationForm: React.FC<{ 
    viewKey?: string,
    linkedItem?: { [key: string]: number },
    searchValue?: string
}> = ({ 
    viewKey, linkedItem, searchValue  
}) => {

    const apiObject = 'location';

    // Parse the location parts from the search value
    const parseLocationSearchValue = (searchValue: string): {
        street?: string;
        house_number?: string;
        postal_code?: string;
        city?: string;
    } => {
        if (!searchValue) return {};

        // Clean the input
        const cleaned = searchValue
            .trim()
            .replace(/\s+/g, ' ')
            .replace(/\s?,\s?/g, ',')
            .replace(/,+/g, ',');

        let streetPart = cleaned;
        let postalCode: string | undefined;
        let city: string | undefined;

        // Extract the postal code in Dutch format
        const postcodeRegex = /(\d{4}\s?[A-Za-z]{2})/;
        const postcodeMatch = cleaned.match(postcodeRegex);

        if (postcodeMatch) {
            postalCode = postcodeMatch[1]
                .toUpperCase()
                .replace(/\s?/, ' ')
                .trim();

            const beforePostcode = cleaned.substring(0, postcodeMatch.index).replace(/,$/, '').trim();
            const afterPostcode = cleaned.substring((postcodeMatch.index ?? 0) + postcodeMatch[1].length).replace(/^,/, '').trim();

            streetPart = beforePostcode;

            if (afterPostcode) {
                city = afterPostcode;
            }
        } else {
            const parts = cleaned.split(',');
            if (parts.length >= 2) {
                streetPart = parts[0].trim();
                city = parts[1].trim();
            }
        }

        // Extract street and house number
        let street: string | undefined;
        let houseNumber: string | undefined;

        const houseRegex = /(.+?)\s+(\d+\s?[A-Za-z\-]*)$/;
        const houseMatch = streetPart.match(houseRegex);

        if (houseMatch) {
            street = houseMatch[1].trim();
            houseNumber = houseMatch[2].replace(/\s+/g, '');
        } else {
            street = streetPart.trim();
        }

        return {
            street,
            house_number: houseNumber,
            postal_code: postalCode,
            city
        };
    };

    // Parse the location values from the search value
    const parsedSearchValue = searchValue ? parseLocationSearchValue(searchValue) : null;

    // Plug in the fields and their properties:
    const fields: FieldType[] = [
        {
            syncWithUpdatedData: true,
            name: 'street',
            label: 'location.general.street_label',
            type: 'text',
            placeholder: 'location.general.street_placeholder',
            ...(parsedSearchValue?.street ? { defaultValue: parsedSearchValue.street } : {}),
            group: 'street-row',
            width: '70%'
        },
        {
            name: 'house_number',
            label: 'location.general.house_number_label_short',
            type: 'text',
            placeholder: 'location.general.house_number_placeholder',
            ...(parsedSearchValue?.house_number ? { defaultValue: parsedSearchValue.house_number } : {}),
            group: 'street-row',
            width: '30%'
        },
        {
            name: 'postal_code',
            label: 'location.general.postal_code_label',
            type: 'text',
            placeholder: 'location.general.postal_code_placeholder',
            ...(parsedSearchValue?.postal_code ? { defaultValue: parsedSearchValue.postal_code } : {}),
            group: 'city-row',
            width: '35%'
        },
        {
            syncWithUpdatedData: true,
            name: 'city',
            label: 'location.general.city_label',
            type: 'text',
            placeholder: 'location.general.city_placeholder',
            ...(parsedSearchValue?.city ? { defaultValue: parsedSearchValue.city } : {}),
            group: 'city-row',
            width: '65%'
        },
        {
            type: 'address-lookup',
        },
        {
            syncWithUpdatedData: true,
            name: 'country',
            label: 'location.general.country_label',
            type: 'text',
            placeholder: 'location.general.country_placeholder',
        },
        {
            name: 'name',
            label: 'location.general.location_name_label',
            type: 'text',
            placeholder: 'location.general.location_name_placeholder',
            tooltipText: 'location.general.location_name_tooltipText',
        },
    ];

    return (
        <FormModal 
            viewKey={viewKey}
            apiObject={apiObject}
            fields={fields}
        />
    );
};

export default LocationForm;